'use client';

import { ArrayElement, GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';
import { font } from '@/theme/zeelool.theme';
import { env } from '@/utils/env';
import { setSession } from '@/utils/tools';
import { Collapse, List, ListItemButton, ListItemText } from '@mui/material';
import { CollapseArrowDownOutlined } from '@zeelool/icon';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { LEGAL_LINKS_TEMPLATE_ID } from '../2026-pc/legalLinks';
import { PAYMENT_TEMPLATE_ID } from '../2026-pc/paymentModules';
import { COPYRIGHT_TEMPLATE_ID } from '../2026-pc/copyrightModules';

type FooterNavItem = ArrayElement<GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>>;

interface FooterNavContent {
  layout_title: string;
  list: Array<{ title: string; id: string; href: string }>;
}

const NavList = ({
  navList,
}: {
  navList: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
}) => {
  const [openItemId, setOpenItemId] = useState<number | null>(null);

  const handleNavItemToggle = (itemId: number) => {
    setOpenItemId((prevOpenId) => (prevOpenId === itemId ? null : itemId));
  };

  return (
    <List
      component="nav"
      sx={{
        width: '100%',
        padding: '0 0 8px',
        '& .MuiListItemText-root': {
          margin: 0,
        },
        '& .footer-nav-trigger': {
          minHeight: '48px',
          padding: 0,
        },
        '& .footer-nav-trigger .MuiTypography-root': {
          color: '#fff',
          fontFamily: font.bs,
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: 1,
        },
        '& .footer-nav-link': {
          minHeight: '40px',
          padding: 0,
        },
        '& .footer-nav-link .MuiTypography-root': {
          color: '#fff',
          fontFamily: font.br,
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '14px',
        },
      }}
    >
      {navList.map((item, firstLevelIndex) => {
        // 专用模板在 Footer 底部展示，保留普通导航在原响应中的埋点索引。
        if (
          item.template_id === LEGAL_LINKS_TEMPLATE_ID ||
          item.template_id === PAYMENT_TEMPLATE_ID ||
          item.template_id === COPYRIGHT_TEMPLATE_ID
        )
          return null;
        return (
          <NavItemCollapse
            key={item.id}
            navItem={item}
            firstLevelIndex={firstLevelIndex}
            isOpen={openItemId === item.id}
            onToggle={() => handleNavItemToggle(item.id!)}
          />
        );
      })}
    </List>
  );
};

const NavItemCollapse = ({
  navItem,
  firstLevelIndex,
  isOpen,
  onToggle,
}: {
  navItem: FooterNavItem;
  firstLevelIndex: number;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const { trackEvent } = useReporting();
  const router = useRouter();
  const { cnName } = useRouteMetadata();
  const content = navItem.content as FooterNavContent;

  if ((navItem.template_id != null && navItem.template_id !== 1) || !Array.isArray(content?.list))
    return null;

  const handleClickNavItem = (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: { title: string; id: string; href: string },
    secondLevelIndex: number,
  ) => {
    event.preventDefault();
    trackEvent(
      {
        eventName: 'BottomNavTab_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
          first_tab_name: content.layout_title,
          second_tab_name: item.title,
          first_tab_index: firstLevelIndex,
          second_tab_index: secondLevelIndex,
        },
      },
      [ProviderType.SENSORS],
    );
    if (item.href.includes('goods-list')) {
      setSession('zl_goodsListEnterPageScene', '全局导航');
    }
    if (item.href === 'https://cookie.zeelool.com') {
      window.OneTrust.ToggleInfoDisplay();
    } else if (
      (item.href.includes('zeelool.com') || item.href.includes('xmslol.cn')) &&
      !item.href.includes(env.NEXT_PUBLIC_HELP_CENTER_PATH)
    ) {
      const href = new URL(item.href);
      router.push(href.href.replace(href.origin, ''));
    } else {
      window.location.href = item.href;
    }
  };

  return (
    <div className="border-b border-solid border-[#808080] last:border-b-0">
      <ListItemButton
        className="footer-nav-trigger"
        aria-expanded={isOpen}
        onClick={() => {
          onToggle();
          trackEvent(
            {
              eventName: 'BottomNavTab_Click',
              properties: {
                first_tab_name: content.layout_title,
                first_tab_index: firstLevelIndex,
                page_name: cnName,
                url_path: window.location.href,
              },
            },
            [ProviderType.SENSORS],
          );
        }}
      >
        <ListItemText primary={content.layout_title} />
        <CollapseArrowDownOutlined
          className={`size-[18px] text-primary-white transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          aria-hidden
        />
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {content.list.map((item, index) =>
            typeof item?.href === 'string' && item.href.trim() ? (
              <ListItemButton className="footer-nav-link" key={`${item.href}-${index}`}>
                <ListItemText>
                  <Link
                    href={item.href}
                    onClick={(event) => handleClickNavItem(event, item, index)}
                  >
                    {item.title}
                  </Link>
                </ListItemText>
              </ListItemButton>
            ) : null,
          )}
        </List>
      </Collapse>
    </div>
  );
};

export default NavList;
